// File: frontend/src/contexts/LanguageContext.js

import React, { createContext, useState, useContext, useEffect } from 'react';

// Import local translations
import enCaTranslations from '../locales/en-ca.json';
import esEsTranslations from '../locales/es-es.json';

// Supported locales configuration
const SUPPORTED_LOCALES = {
  'en-ca': {
    name: 'English (Canada)',
    languageCode: 'en',
    countryCode: 'ca',
    flag: '🇨🇦'
  },
  'es-es': {
    name: 'Español (España)',
    languageCode: 'es', 
    countryCode: 'es',
    flag: '🇪🇸'
  }
};

const DEFAULT_LOCALE = 'en-ca';

// Local translations object
const localTranslations = {
  'en-ca': enCaTranslations,
  'es-es': esEsTranslations
};

// Initialize translations with local files
let translations = { ...localTranslations };

const LanguageContext = createContext();

const getBrowserLanguage = () => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  
  const browserLang = navigator.language || navigator.userLanguage;
  const shortLang = browserLang.split('-')[0];
  
  // Check if the exact browser language is supported
  if (SUPPORTED_LOCALES[browserLang]) {
    return browserLang;
  }
  
  // Check for language match (e.g., 'es' matches 'es-es')
  if (shortLang === 'es') {
    return 'es-es';
  }
  
  // Default to en-ca for English or any other language
  return DEFAULT_LOCALE;
};

// String interpolation function for dynamic content
const interpolateString = (str, params) => {
  if (!params || typeof str !== 'string') return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    return params[key] !== undefined ? params[key] : `{{${key}}}`;
  });
};

export const LanguageProvider = ({ children }) => {
  // Initialize language from localStorage or browser
  const [language, setLanguage] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LOCALE;
    
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && SUPPORTED_LOCALES[savedLanguage]) {
      return savedLanguage;
    }
    
    return getBrowserLanguage();
  });
  
  const [currentTranslations, setCurrentTranslations] = useState(translations[language]);

  // Update translations and localStorage when language changes
  useEffect(() => {
    setCurrentTranslations(translations[language]);
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', language);
      document.documentElement.lang = SUPPORTED_LOCALES[language].languageCode;
    }
  }, [language]);

  const changeLanguage = (newLanguage) => {
    if (!SUPPORTED_LOCALES[newLanguage]) {
      console.warn(`Language ${newLanguage} is not supported.`);
      return;
    }
    
    setLanguage(newLanguage);
  };

  const translate = (key, params = null) => {
    try {
      const translatedText = currentTranslations?.[key] || key;
      return params ? interpolateString(translatedText, params) : translatedText;
    } catch (error) {
      console.error('Translation error:', error);
      return key;
    }
  };

  const value = {
    language,
    changeLanguage,
    t: translate,
    supportedLanguages: Object.keys(SUPPORTED_LOCALES),
    supportedLocales: SUPPORTED_LOCALES,
    currentLanguage: language,
    languageInfo: SUPPORTED_LOCALES[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
